import {Component, computed, inject, effect} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {ToastModule} from 'primeng/toast';
import {TooltipModule} from 'primeng/tooltip';
import {TranslocoPipe} from '@jsverse/transloco';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {MultiTabStepBaseComponent} from '../../../../../../shared/tabs/multi-tab-step-base.component';
import {TabStatus} from '../../../../../../shared/tabs/tab-status.model';
import {TabStatusDirective} from '../../../../../../shared/tabs/tab-status.directive';
import {
  ProjectChapterType,
  ProjectStepType,
  TenderInvitationPreviewControllerService,
  TenderInvitationPreviewResponse,
  TenderPhaseType,
} from '@frontend/shared';
import {PreviousStepIncompleteNoticeComponent} from '../../../../../../shared/components/previous-step-incomplete-notice/previous-step-incomplete-notice.component';
import {rxResource} from '@angular/core/rxjs-interop';
import {
  RFC_MAIL_PREVIEW_SECTIONS,
  RFC_MAIL_PREVIEW_FIELD_MAP,
  Step921RfcMailPreviewComponent,
} from './step-9-2-1-rfc-mail-preview/step-9-2-1-rfc-mail-preview.component';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {CardTitleComponent} from '../../../../../../shared/components/header/card-title/card-title.component';
import {Step922ProjectDecisionComponent} from './step-9-2-project-decision/step-9-2-2-project-decision.component';
import {calculateRichTextSectionStatus} from '../../../../../../shared/services/project-step-rich-text.service';
import {createTabStatusSignal} from '../../../../../../shared/tabs/tab-utils';
import {TabsUnsavedRevealDirective} from '../../../../../../shared/tabs/tabs-unsaved-reveal.directive';

@Component({
  imports: [
    FormsModule,
    ButtonModule,
    CardModule,
    ToastModule,
    TooltipModule,
    TranslocoPipe,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    PreviousStepIncompleteNoticeComponent,
    Step921RfcMailPreviewComponent,
    LoadingComponent,
    Step922ProjectDecisionComponent,
    CardTitleComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './step-9-2-project-decision-preview.component.html',
})
export class Step92ProjectDecisionPreviewComponent extends MultiTabStepBaseComponent {
  private readonly tenderInvitationPreviewService = inject(
    TenderInvitationPreviewControllerService,
  );
  protected readonly mailPreviewTabStatus = createTabStatusSignal();
  protected readonly projectDecisionStatus = createTabStatusSignal();

  protected readonly isStepPrerequisiteCompleted = computed(() =>
    this.stepState.areStepsCompleted([
      {
        chapterId: ProjectChapterType.RFQ,
        stepId: ProjectStepType.RFQ_AGGREGATED_RESULTS,
      },
    ]),
  );

  readonly invitationPreviewResource = rxResource({
    params: () => {
      if (!this.projectId || !this.isStepPrerequisiteCompleted()) {
        return null;
      }
      return {projectId: this.projectId};
    },
    stream: ({params: request}) =>
      this.tenderInvitationPreviewService.getTenderInvitationPreview({
        projectId: request!.projectId,
        phase: TenderPhaseType.RFC,
      }),
  });

  constructor() {
    super();

    effect(() => {
      const invitationData = this.invitationPreviewResource.value();
      if (invitationData) {
        this.calculateMailPreviewTabStatuses(invitationData);
      }
    });
  }

  private calculateMailPreviewTabStatuses(invitationData: TenderInvitationPreviewResponse): void {
    const mailCompleted = calculateRichTextSectionStatus(
      invitationData,
      RFC_MAIL_PREVIEW_SECTIONS,
      RFC_MAIL_PREVIEW_FIELD_MAP,
    );

    const mailStatus: TabStatus = {
      totalSections: RFC_MAIL_PREVIEW_SECTIONS.length,
      completedSections: mailCompleted,
    };

    this.onMailPreviewStatusChange(mailStatus);
  }

  onMailPreviewStatusChange(status: TabStatus): void {
    this.mailPreviewTabStatus.update(status);
  }

  protected updateStepState(status: TabStatus) {
    this.projectDecisionStatus.update(status);
    void this.stepState.loadStepStatuses(this.projectId);
  }
}
